import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, Save, MapPin } from 'lucide-react';
import { API } from '../config';
import { RegionSelect } from './RegionSelect';

const playfair = "'Playfair Display', serif";
const inputStyle = { background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };

const blank = {
  store_name: '', sender_name: '', sender_phone: '',
  province: '', city: '', district: '',
  postal_code: '', subdistrict_id: '',
  address_line: '', notes: '',
};

export const ShippingOriginManager = () => {
  const [form, setForm] = useState(blank);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exists, setExists] = useState(false);
  // Debug panel state
  const [dbg, setDbg] = useState({
    dest_province: 'BANTEN', dest_city: 'KABUPATEN PANDEGLANG', dest_district: 'PANDEGLANG',
    dest_postal_code: '42271', dest_subdistrict_id: '', weight_grams: 1000,
    courier: 'jne', service: 'OKE',
  });
  const [dbgResult, setDbgResult] = useState(null);
  const [dbgLoading, setDbgLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/shipping/origin`, { withCredentials: true })
      .then((r) => {
        const d = r.data || {};
        if (d.province) {
          setForm({ ...blank, ...d });
          setExists(true);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    for (const k of ['store_name', 'sender_name', 'sender_phone', 'province', 'city', 'district', 'postal_code']) {
      if (!String(form[k] || '').trim()) {
        toast.error(`Field "${k}" wajib diisi`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Convert subdistrict_id to int or strip
      if (payload.subdistrict_id === '' || payload.subdistrict_id === null) delete payload.subdistrict_id;
      else payload.subdistrict_id = parseInt(payload.subdistrict_id, 10);
      await axios.put(`${API}/admin/shipping/origin`, payload, { withCredentials: true });
      toast.success('Alamat asal pengiriman tersimpan');
      setExists(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p style={{ color: '#A3B8AC' }}>Loading…</p>;

  return (
    <div data-testid="shipping-origin-manager">
      <div className="flex items-center gap-2 mb-2">
        <Truck className="w-5 h-5" style={{ color: '#D4AF37' }} />
        <h2 className="text-2xl font-light" style={{ fontFamily: playfair, color: '#F8FAF9' }}>Shipping Origin / Alamat Asal Gudang</h2>
      </div>
      <p className="text-sm mb-6" style={{ color: '#A3B8AC' }}>
        Setiap perhitungan ongkir (produk + Kuji) menggunakan alamat ini sebagai titik asal. Wajib diisi sebelum customer bisa checkout.
      </p>

      {!exists && (
        <div className="rounded-2xl p-4 mb-5" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <p className="text-sm font-semibold" style={{ color: '#EF4444' }}>⚠️ Belum diatur — semua perhitungan ongkir akan ditolak</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 rounded-2xl p-5 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div>
          <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Store / Warehouse Name *</label>
          <input value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="origin-store-name" />
        </div>
        <div>
          <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Sender Name *</label>
          <input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="origin-sender-name" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Sender Phone *</label>
          <input value={form.sender_phone} onChange={(e) => setForm({ ...form, sender_phone: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="origin-sender-phone" />
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs mb-2" style={{ color: '#A3B8AC' }}>
            <MapPin className="w-3 h-3 inline mr-1" />
            Lokasi (Provinsi · Kota · Kecamatan)
          </label>
          <RegionSelect
            value={{ province: form.province, city: form.city, district: form.district }}
            onChange={(next) => setForm({ ...form, province: next.province, city: next.city, district: next.district })}
            required
          />
        </div>

        <div>
          <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Postal Code *</label>
          <input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="origin-postal-code" />
        </div>
        <div>
          <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>RajaOngkir Subdistrict ID (opsional, untuk tarif real-time)</label>
          <input value={form.subdistrict_id || ''} onChange={(e) => setForm({ ...form, subdistrict_id: e.target.value.replace(/\D/g, '') })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} placeholder="cth: 68523" data-testid="origin-subdistrict-id" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Address line (opsional)</label>
          <input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="origin-address-line" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Notes (opsional)</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="2" className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} />
        </div>
      </div>

      <button onClick={save} disabled={saving} className="mt-5 px-6 py-3 rounded-full font-semibold flex items-center gap-2 disabled:opacity-50" style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="origin-save-btn">
        <Save className="w-4 h-4" /> {saving ? 'Menyimpan…' : 'Simpan Alamat Asal'}
      </button>

      {/* ===== Admin Shipping Debug Panel ===== */}
      <div className="mt-10 rounded-2xl p-5 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="shipping-debug-panel">
        <h3 className="text-xl font-light mb-1" style={{ fontFamily: playfair, color: '#F8FAF9' }}>Shipping Debug Panel</h3>
        <p className="text-xs mb-4" style={{ color: '#A3B8AC' }}>Tes kuota tarif real-time + lihat detail kalkulasi (admin-only).</p>

        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <input value={dbg.dest_province} onChange={(e) => setDbg({ ...dbg, dest_province: e.target.value })} className="rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} placeholder="Dest Province" data-testid="dbg-dest-province" />
          <input value={dbg.dest_city} onChange={(e) => setDbg({ ...dbg, dest_city: e.target.value })} className="rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} placeholder="Dest City" data-testid="dbg-dest-city" />
          <input value={dbg.dest_district} onChange={(e) => setDbg({ ...dbg, dest_district: e.target.value })} className="rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} placeholder="Dest District" data-testid="dbg-dest-district" />
          <input value={dbg.dest_postal_code} onChange={(e) => setDbg({ ...dbg, dest_postal_code: e.target.value })} className="rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} placeholder="Dest Postal Code" data-testid="dbg-dest-postal" />
          <input value={dbg.dest_subdistrict_id} onChange={(e) => setDbg({ ...dbg, dest_subdistrict_id: e.target.value.replace(/\D/g,'') })} className="rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} placeholder="Dest Subdistrict ID" data-testid="dbg-dest-subdistrict-id" />
          <input type="number" value={dbg.weight_grams} onChange={(e) => setDbg({ ...dbg, weight_grams: parseInt(e.target.value, 10) || 0 })} className="rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} placeholder="Weight (gram)" data-testid="dbg-weight" />
          <input value={dbg.courier} onChange={(e) => setDbg({ ...dbg, courier: e.target.value.toLowerCase() })} className="rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} placeholder="Courier (jne)" />
          <input value={dbg.service} onChange={(e) => setDbg({ ...dbg, service: e.target.value.toUpperCase() })} className="rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} placeholder="Service (OKE / REG / YES)" />
        </div>

        <button
          onClick={async () => {
            setDbgLoading(true);
            try {
              const { data } = await axios.get(`${API}/admin/shipping/debug`, {
                params: {
                  weight_grams: dbg.weight_grams,
                  dest_province: dbg.dest_province, dest_city: dbg.dest_city, dest_district: dbg.dest_district,
                  dest_postal_code: dbg.dest_postal_code,
                  dest_subdistrict_id: dbg.dest_subdistrict_id || undefined,
                  courier: dbg.courier, service: dbg.service,
                },
                withCredentials: true,
              });
              setDbgResult(data);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Debug fetch failed');
            } finally {
              setDbgLoading(false);
            }
          }}
          disabled={dbgLoading}
          className="px-5 py-2 rounded-full font-semibold disabled:opacity-50 text-sm"
          style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37', border: '1px solid #D4AF37' }}
          data-testid="run-shipping-debug-btn"
        >
          {dbgLoading ? 'Querying…' : 'Run Shipping Debug'}
        </button>

        {dbgResult && (
          <div className="mt-4 rounded-lg p-4 text-xs space-y-1" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(212,175,55,0.2)' }} data-testid="shipping-debug-result">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <span style={{ color: '#A3B8AC' }}>Origin Province:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.origin.province || '—'}</span>
              <span style={{ color: '#A3B8AC' }}>Origin City:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.origin.city || '—'}</span>
              <span style={{ color: '#A3B8AC' }}>Origin District:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.origin.district || '—'}</span>
              <span style={{ color: '#A3B8AC' }}>Origin Postal:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.origin.postal_code || '—'}</span>
              <span style={{ color: '#A3B8AC' }}>Origin Subdistrict ID:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.origin.subdistrict_id || '— (belum diisi)'}</span>
              <div className="col-span-2 my-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} />
              <span style={{ color: '#A3B8AC' }}>Destination Province:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.destination.province || '—'}</span>
              <span style={{ color: '#A3B8AC' }}>Destination City:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.destination.city || '—'}</span>
              <span style={{ color: '#A3B8AC' }}>Destination District:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.destination.district || '—'}</span>
              <span style={{ color: '#A3B8AC' }}>Destination Postal:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.destination.postal_code || '—'}</span>
              <span style={{ color: '#A3B8AC' }}>Destination Subdistrict ID:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.destination.subdistrict_id || '—'}</span>
              <div className="col-span-2 my-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} />
              <span style={{ color: '#A3B8AC' }}>Weight:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.weight_grams} g</span>
              <span style={{ color: '#A3B8AC' }}>Courier:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.courier?.toUpperCase()}</span>
              <span style={{ color: '#A3B8AC' }}>Service:</span><span style={{ color: '#F8FAF9' }}>{dbgResult.service || '—'}</span>
              <span style={{ color: '#A3B8AC' }}>Rate Source:</span>
              <span className="font-semibold" style={{ color: dbgResult.rate_source === 'rajaongkir' ? '#22C55E' : '#F59E0B' }} data-testid="dbg-rate-source">
                {dbgResult.rate_source}
              </span>
              <span style={{ color: '#A3B8AC' }}>Final Cost:</span>
              <span className="font-semibold" style={{ color: '#D4AF37' }} data-testid="dbg-final-cost">
                {dbgResult.final_cost ? `Rp ${dbgResult.final_cost.toLocaleString('id-ID')}` : '— (manual)'}
              </span>
            </div>
            {dbgResult.message && (
              <p className="mt-2 italic" style={{ color: '#F59E0B' }}>{dbgResult.message}</p>
            )}
            {dbgResult.error && (
              <p className="mt-1 break-words" style={{ color: '#EF4444' }}>Error: {dbgResult.error}</p>
            )}
            {dbgResult.all_services?.length > 0 && (
              <div className="mt-3 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="mb-1" style={{ color: '#A3B8AC' }}>All Services Returned:</p>
                {dbgResult.all_services.map((s) => (
                  <div key={s.service} className="flex justify-between">
                    <span style={{ color: '#F8FAF9' }}>{s.name} ({s.eta_days} hari)</span>
                    <span style={{ color: '#D4AF37' }}>Rp {s.cost.toLocaleString('id-ID')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
